import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["documents"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      subjectDocumentsList: build.query<
        SubjectDocumentsListApiResponse,
        SubjectDocumentsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/documents/subjects/${queryArg.subjectType}/${queryArg.subjectId}/documents/`,
        }),
        providesTags: ["documents"],
      }),
      subjectDocumentsCreate: build.mutation<
        SubjectDocumentsCreateApiResponse,
        SubjectDocumentsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/documents/subjects/${queryArg.subjectType}/${queryArg.subjectId}/documents/`,
          method: "POST",
          body: queryArg.documentCreationRequest,
        }),
        invalidatesTags: ["documents"],
      }),
      subjectDocumentsDownload: build.query<
        SubjectDocumentsDownloadApiResponse,
        SubjectDocumentsDownloadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/documents/subjects/${queryArg.subjectType}/${queryArg.subjectId}/documents/${queryArg.id}/download/`,
        }),
        providesTags: ["documents"],
      }),
      subjectDocumentsPreview: build.query<
        SubjectDocumentsPreviewApiResponse,
        SubjectDocumentsPreviewApiArg
      >({
        query: (queryArg) => ({
          url: `/api/documents/subjects/${queryArg.subjectType}/${queryArg.subjectId}/documents/${queryArg.id}/preview/`,
        }),
        providesTags: ["documents"],
      }),
      subjectDocumentsRefresh: build.mutation<
        SubjectDocumentsRefreshApiResponse,
        SubjectDocumentsRefreshApiArg
      >({
        query: (queryArg) => ({
          url: `/api/documents/subjects/${queryArg.subjectType}/${queryArg.subjectId}/documents/${queryArg.id}/refresh/`,
          method: "PATCH",
          body: queryArg.patchedDocumentInstance,
        }),
        invalidatesTags: ["documents"],
      }),
      subjectDocumentsResend: build.mutation<
        SubjectDocumentsResendApiResponse,
        SubjectDocumentsResendApiArg
      >({
        query: (queryArg) => ({
          url: `/api/documents/subjects/${queryArg.subjectType}/${queryArg.subjectId}/documents/${queryArg.id}/resend/`,
          method: "POST",
          body: queryArg.documentInstance,
        }),
        invalidatesTags: ["documents"],
      }),
      subjectDocumentsSend: build.mutation<
        SubjectDocumentsSendApiResponse,
        SubjectDocumentsSendApiArg
      >({
        query: (queryArg) => ({
          url: `/api/documents/subjects/${queryArg.subjectType}/${queryArg.subjectId}/documents/${queryArg.id}/send/`,
          method: "POST",
          body: queryArg.documentInstance,
        }),
        invalidatesTags: ["documents"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type SubjectDocumentsListApiResponse =
  /** status 200  */ DocumentInstanceRead[];
export type SubjectDocumentsListApiArg = {
  /** UUID of the subject */
  subjectId: string;
  /** Type of subject (matter or intake) */
  subjectType: "intake" | "matter";
};
export type SubjectDocumentsCreateApiResponse =
  /** status 200  */ DocumentInstanceRead;
export type SubjectDocumentsCreateApiArg = {
  /** UUID of the subject */
  subjectId: string;
  /** Type of subject (matter or intake) */
  subjectType: "intake" | "matter";
  documentCreationRequest: DocumentCreationRequest;
};
export type SubjectDocumentsDownloadApiResponse = unknown;
export type SubjectDocumentsDownloadApiArg = {
  /** UUID of the document instance */
  id: string;
  /** UUID of the subject */
  subjectId: string;
  /** Type of subject (matter or intake) */
  subjectType: "intake" | "matter";
};
export type SubjectDocumentsPreviewApiResponse = unknown;
export type SubjectDocumentsPreviewApiArg = {
  /** UUID of the document instance */
  id: string;
  /** UUID of the subject */
  subjectId: string;
  /** Type of subject (matter or intake) */
  subjectType: "intake" | "matter";
};
export type SubjectDocumentsRefreshApiResponse =
  /** status 200  */ DocumentInstanceRead;
export type SubjectDocumentsRefreshApiArg = {
  /** UUID of the document instance */
  id: string;
  /** UUID of the subject */
  subjectId: string;
  /** Type of subject (matter or intake) */
  subjectType: "intake" | "matter";
  patchedDocumentInstance: PatchedDocumentInstance;
};
export type SubjectDocumentsResendApiResponse =
  /** status 200  */ DocumentInstanceRead;
export type SubjectDocumentsResendApiArg = {
  /** UUID of the document instance */
  id: string;
  /** UUID of the subject */
  subjectId: string;
  /** Type of subject (matter or intake) */
  subjectType: "intake" | "matter";
  documentInstance: DocumentInstance;
};
export type SubjectDocumentsSendApiResponse =
  /** status 200  */ DocumentInstanceRead;
export type SubjectDocumentsSendApiArg = {
  /** UUID of the document instance */
  id: string;
  /** UUID of the subject */
  subjectId: string;
  /** Type of subject (matter or intake) */
  subjectType: "intake" | "matter";
  documentInstance: DocumentInstance;
};
export type DocumentInstance = {
  /** * `CREATED` - Created
   * `SENT` - Sent
   * `COMPLETED` - Completed
   * `VOIDED` - Voided
   * `SIGNED` - Signed */
  status: "CREATED" | "SENT" | "COMPLETED" | "VOIDED" | "SIGNED";
  external_document_reference: string;
  requires_signatures?: boolean;
  detailed_agreement_info?: any;
  /** Whether this document was auto-generated by a workflow */
  auto_generated?: boolean;
  /** Snapshot of field values after validation/prefill when the document was created. */
  field_values?: any;
  merge_fields?: any;
  /** Source of field values: 'system' for AI/FinchGPT or user UUID */
  field_values_source?: string | null;
};
/**
 * Embedded v2 Template data returned by the document instance serializer.
 * NOTE: This type was manually added to replace `{ [key: string]: any }`.
 * It will be overwritten on next `pnpm generate:api` — update the OpenAPI
 * spec to make this permanent.
 */
export type EmbeddedDocumentTemplate = {
  id: string;
  name: string;
  display_name?: string | null;
  format: "PDF" | "DOCX" | "HTML" | "SMS" | "EXTERNAL";
  category:
    | "HIPAA_COVERSHEET"
    | "HIPAA_AUTHORIZATION"
    | "FOIA_LETTER"
    | "LIEN_NOTICE"
    | "RETAINER"
    | "LOR_GENERIC"
    | "LOR_FIRST_PARTY"
    | "LOR_THIRD_PARTY"
    | "LOR_HEALTH"
    | "WELCOME_LETTER"
    | "DROP_LETTER"
    | "JOINT_VENTURE_NOTICE"
    | "CLIENT_COMMUNICATION"
    | "MEDICAL_RECORDS_REQUEST"
    | "AFFIDAVIT"
    | "LETTER_OF_PROTECTION"
    | "PRESERVATION_LETTER"
    | "PIP_DEMAND"
    | "OTHER";
  scope?: "GLOBAL" | "FIRM";
  status?: "ACTIVE" | "SUPERSEDED" | "ARCHIVED";
  requires_signatures?: boolean;
  is_default?: boolean;
  version?: number;
  version_group?: string;
  firm_id?: string | null;
  attorney_id?: string | null;
};
export type DocumentInstanceRead = {
  id: string;
  /** * `CREATED` - Created
   * `SENT` - Sent
   * `COMPLETED` - Completed
   * `VOIDED` - Voided
   * `SIGNED` - Signed */
  status: "CREATED" | "SENT" | "COMPLETED" | "VOIDED" | "SIGNED";
  external_document_reference: string;
  /** Return v2 Template data. Raises if v2_template is missing. */
  document_template: EmbeddedDocumentTemplate | null;
  requires_signatures?: boolean;
  detailed_agreement_info?: any;
  created_at: string;
  /** Whether this document was auto-generated by a workflow */
  auto_generated?: boolean;
  /** Snapshot of field values after validation/prefill when the document was created. */
  field_values?: any;
  merge_fields?: any;
  uploaded_artifact_id: string | null;
  cached_pdf_url: string | null;
  /** Source of field values: 'system' for AI/FinchGPT or user UUID */
  field_values_source?: string | null;
  uploaded_filename: string | null;
};
export type DocumentCreationRequest = {
  document_template_id: string;
  merge_fields: {
    [key: string]: any;
  };
  allow_duplicate?: boolean;
};
export type PatchedDocumentInstance = {
  /** * `CREATED` - Created
   * `SENT` - Sent
   * `COMPLETED` - Completed
   * `VOIDED` - Voided
   * `SIGNED` - Signed */
  status?: "CREATED" | "SENT" | "COMPLETED" | "VOIDED" | "SIGNED";
  external_document_reference?: string;
  requires_signatures?: boolean;
  detailed_agreement_info?: any;
  /** Whether this document was auto-generated by a workflow */
  auto_generated?: boolean;
  /** Snapshot of field values after validation/prefill when the document was created. */
  field_values?: any;
  merge_fields?: any;
  /** Source of field values: 'system' for AI/FinchGPT or user UUID */
  field_values_source?: string | null;
};
export type PatchedDocumentInstanceRead = {
  id?: string;
  /** * `CREATED` - Created
   * `SENT` - Sent
   * `COMPLETED` - Completed
   * `VOIDED` - Voided
   * `SIGNED` - Signed */
  status?: "CREATED" | "SENT" | "COMPLETED" | "VOIDED" | "SIGNED";
  external_document_reference?: string;
  /** Return v2 Template data. Raises if v2_template is missing. */
  document_template?: EmbeddedDocumentTemplate | null;
  requires_signatures?: boolean;
  detailed_agreement_info?: any;
  created_at?: string;
  /** Whether this document was auto-generated by a workflow */
  auto_generated?: boolean;
  /** Snapshot of field values after validation/prefill when the document was created. */
  field_values?: any;
  merge_fields?: any;
  uploaded_artifact_id?: string | null;
  cached_pdf_url?: string | null;
  /** Source of field values: 'system' for AI/FinchGPT or user UUID */
  field_values_source?: string | null;
  uploaded_filename?: string | null;
};
export const {
  useSubjectDocumentsListQuery,
  useSubjectDocumentsCreateMutation,
  useSubjectDocumentsDownloadQuery,
  useSubjectDocumentsPreviewQuery,
  useSubjectDocumentsRefreshMutation,
  useSubjectDocumentsResendMutation,
  useSubjectDocumentsSendMutation,
} = injectedRtkApi;
